/* eslint-disable no-plusplus */
/* eslint-disable no-console */
import cookie from 'js-cookie'
import brightcoveIds from '../config/brightcove-ids'
import { getBrowserName, isIphone, isMobile } from '../../../utils/device'
import { VOD_MESSAGE_TYPES, VOD_AUTOPLAY_ENABLED, STV_COOKIES } from '../../../../constants'
import { initConvivaTracking, resetConvivaAnalytics, trackEvent } from '../../../../utils/dtmHelpers'
import kantarAnalytics from '../analytics/kantar'
import qualityOfServiceLogging from '../analytics/sumo'
import requestVodContent from './request-vod-content'
import prepareVideoWithAds from './prepare-video'
import setupEndboard from './endboard'
import getCurrentEpisodeFromStore from './get-video-data'
import getVideoFromBrightcove from './get-video-from-brightcove'
import { clearStopResumePosition } from '../../../../utils/stopResume'
import {
  getPlayerInfo, getProgrammeMetadata, convivaAdHandlers, initialiseAdAnalytics, getBreakType,
} from '../analytics/convivaAnalyitcs'
import { sendNestedGoogleTagEvent } from '../../../../utils/google-tag-manager-helpers'

const isProduction = window.parent.STV.environment === 'production'

const STOP_RESUME_INTERVAL = 10000 // 10 seconds in milliseconds

const {
  PLAYER_ERROR,
  STOP_RESUME_SAVE_REQUEST,
  PLAYING,
} = VOD_MESSAGE_TYPES

function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

export default async function initVodPlayer({ selector }) {
  initConvivaTracking(cookie.get(STV_COOKIES.STV_USERID), isProduction)
  const postMessageOrigin = window.location.origin
  const node = document.querySelector(selector)
  let groupToken = cookie.get(STV_COOKIES.STV_GROUPTOKEN)
  if (!groupToken || groupToken === 'undefined') {
    groupToken = cookie.get(STV_COOKIES.STV_UNREGISTERED_GROUPTOKEN)
  }
  if (!groupToken || groupToken === 'undefined') {
    groupToken = '0010'
  }

  // Add beforeunload listener to end Conviva session when navigating away
  window.addEventListener('beforeunload', () => {
    window?.ConvivaVideoAnalytics.reportPlaybackEnded()
    resetConvivaAnalytics()
  })

  let videoData
  try {
    videoData = await getCurrentEpisodeFromStore({ groupToken })
  } catch (error) {
    reportError({
      success: false,
      message: error.message,
    })
    return
  }

  const isMobileDevice = isMobile()
  const {
    ACCOUNT_ID, PLAYER_ID, AD_CONFIG_ID, CONFIG_ID, POLICY_KEY,
  } = brightcoveIds(videoData.drmEnabled, isProduction)

  let content
  let endboardCuePoint = null

  function reportError(error) {
    // Report fatal error to Conviva
    if (window?.Conviva && window?.ConvivaVideoAnalytics) {
      // Create a custom metadata object with additional error details
      const customMetadata = {
        [window?.Conviva?.Constants?.PLAYER_NAME]: 'player',
        [window?.Conviva?.Constants?.VIEWER_ID]: window?.parent?.digitalData?.user?.userId || '',
        [window?.Conviva?.Constants?.APPLICATION_VERSION]: '1.1.0',
      }
      try {
        window?.ConvivaVideoAnalytics.setContentInfo(customMetadata)
      } catch (e) {
        console.error('Error updating Conviva metadata:', e)
      }
      const errorMsg = error ? error.message : 'Unknown player error'
      // eslint-disable-next-line max-len
      window.ConvivaVideoAnalytics.reportPlaybackError(errorMsg, window.Conviva.Constants.ErrorSeverity.FATAL)
    }

    window.parent.postMessage(
      {
        type: PLAYER_ERROR,
        body: {
          error,
        },
      },
      postMessageOrigin
    )
  }

  try {
    await Promise.all([requestContent(), insertPlayer()])
    const playerInstance = await setupPlayer()
    playbackPreamble(playerInstance, videoData)
    addTracking(playerInstance)
    startPlayBack({ player: playerInstance })
  } catch (error) {
    reportError(error)
  } finally {
    // Ensure spinner is always removed, even if initialisation fails
    node.classList.remove('unloaded')
  }

  /**
   * Get the programme information from player-api
   */
  async function requestContent() {
    try {
      const contentType = window.parent.location.pathname.split('/')[1] === 'episode' ? 'episode' : 'shortform'
      const response = await requestVodContent({ ...videoData, type: contentType })

      if (!response.success) {
        reportError(response?.error)
      }
      // set content state variable for use further down the chain
      content = response.results
    } catch (error) {
      reportError(error)
    }
  }

  /**
   * Insert the BrightCove player script and required player
   * attributes. Resolve once loaded, or time out after 5000ms.
   */
  async function insertPlayer() {
    const player = node.querySelector('video')
    player.setAttribute('data-account', ACCOUNT_ID)
    player.setAttribute('data-player', PLAYER_ID)
    if (isMobileDevice) player.classList.add('no-volume-controls')

    let playerLoadTimeout = null

    const playerRequest = new Promise((resolve) => {
      const playerScriptUrl = `//players.brightcove.net/${ACCOUNT_ID}/${PLAYER_ID}_default/index.min.js`
      const playerScriptEl = document.createElement('script')
      playerScriptEl.src = playerScriptUrl
      playerScriptEl.onload = () => {
        clearTimeout(playerLoadTimeout)
        resolve()
      }
      playerScriptEl.onerror = () => {
        clearTimeout(playerLoadTimeout)
        const error = {
          success: false,
          message: `Failed to load player script from ${playerScriptUrl}`,
        }
        resolve(error)
      }
      document.body.appendChild(playerScriptEl)
    })

    const timeout = new Promise((resolve) => {
      playerLoadTimeout = setTimeout(() => {
        const error = {
          success: false,
          message: `Timed out after 5000ms`,
        }
        reportError(error)
        resolve(error)
      }, 5000)
    })

    return Promise.race([playerRequest, timeout])
  }

  /**
   * Initialise the BrightCove player
   */
  async function setupPlayer() {
    // create the player
    const videoEl = node.querySelector('video')
    const player = window.bc(videoEl)
    player.debug(!isProduction)

    // Initialize volume and muted state from localStorage if available
    try {
      const storedVolume = localStorage.getItem('vod-player-volume')
      const storedMuted = localStorage.getItem('vod-player-muted')

      if (storedVolume !== null) {
        const volume = parseFloat(storedVolume)
        if (!Number.isNaN(volume) && volume >= 0 && volume <= 1) {
          player.volume(volume)
        }
      }

      if (storedMuted !== null) {
        const muted = storedMuted === 'true'
        player.muted(muted)
      }
    } catch (error) {
      throw new Error('Failed to load volume/muted state from localStorage')
    }

    // Add volume and muted state change listeners
    player.on('volumechange', () => {
      try {
        const currentVolume = player.volume()
        const currentMuted = player.muted()
        localStorage.setItem('vod-player-volume', currentVolume.toString())
        localStorage.setItem('vod-player-muted', currentMuted.toString())
      } catch (error) {
        throw new Error('Failed to save volume/muted state to localStorage')
      }
    })

    if (VOD_AUTOPLAY_ENABLED) {
      player.autoplay(true)
    }

    const playerReady = new Promise((resolve) => {
      player.ready(() => {
        initialiseAdAnalytics()
        if (videoData.resumePosition > 0) {
          player.one('loadedmetadata', () => {
            resumePlayback({
              player,
              resumePosition: videoData.resumePosition,
            })
          })
        }
        resolve()
      })
    })

    const getVideoPromise = getVideoFromBrightcove(
      content.video.id,
      ACCOUNT_ID,
      AD_CONFIG_ID,
      POLICY_KEY,
      CONFIG_ID
    )
    const [, json] = await Promise.all([playerReady, getVideoPromise])

    const endCuePoint = json.cue_points.find(cuePoint => cuePoint.name === 'end')
    if (endCuePoint) {
      endboardCuePoint = endCuePoint.time * 1000
    }

    const videoObject = prepareVideoWithAds({
      player,
      video: json,
      content,
      videoData,
      resumePosition: videoData.resumePosition,
    })

    videoObject.sources.forEach((source) => {
      if (!source?.vmap) return
      // Some vmaps are coming back http and breaking safari
      source.vmap = source.vmap.replace('http://', 'https://') // eslint-disable-line no-param-reassign
    })

    // Disable subtitles by default
    videoObject.textTracks.forEach((track) => {
      if (track.kind === 'captions' || track.kind === 'subtitles') {
        // eslint-disable-next-line no-param-reassign
        track.mode = 'disabled'
      }
    })

    player.catalog.load(videoObject)

    setupEndboard(player, videoData)

    return player
  }

  function playbackPreamble(player) {
    const playerEl = player.el()
    addRestartButton({ playerEl })
  }

  function addTracking(player) {
    // Kantar SpringStream BARB analytics
    startKantarAnalytics({ videoData, player })
    const metaData = getProgrammeMetadata(content)
    const playerInfo = getPlayerInfo()
    // add the metadata to setContentInfo when load
    player.on('loadedmetadata', () => {
      window?.ConvivaVideoAnalytics.setContentInfo(metaData)
      window?.ConvivaVideoAnalytics.setPlayerInfo(playerInfo)
    })

    // QoS logging sent to Sumo every 5mins, on error or on ad start
    qualityOfServiceLogging({
      videoStartTime: videoData.resumePosition,
      player,
      episode: content,
      groupToken: videoData.groupToken,
    })
    return Promise.resolve({ player })
  }

  function startPlayBack({ player }) {
    // Add Generic Error handling
    player.on('error', () => {
      const error = player.error()
      player.dispose()
      reportError(error)
    })

    let lastSavedTime = 0 // Tracks the last time the position was saved
    let subtitleState = false // Track the subtitle state

    function saveStopResumeTime(currentTime, clearEntry = false) {
      if (player.paused() || player.ads.isInAdMode()) {
        return
      }

      if (endboardCuePoint && currentTime >= endboardCuePoint - 20000) { // 20 seconds before end
        clearStopResumePosition({ id: videoData.id })
        dispatchStopResumeTimeToParentWindow({
          time: 0,
          postMessageOrigin,
          clearEntry: true,
        })
        return
      }

      const time = parseInt(
        player.ssai().contentTimelineState(player.currentTime()).relativeTime,
        10
      ) * 1000

      const dispatchData = {
        id: videoData.id,
        time,
        postMessageOrigin,
        clearEntry,
      }
      dispatchStopResumeTimeToParentWindow(dispatchData)
    }

    const onTimeUpdate = () => {
      const currentTime = player.currentTime() * 1000 // Convert to milliseconds

      if (currentTime - lastSavedTime >= STOP_RESUME_INTERVAL) {
        saveStopResumeTime(currentTime)
        lastSavedTime = currentTime
      }
    }

    player.on('timeupdate', onTimeUpdate)

    player.on('playing', handlePlaying)
    player.on('pause', () => saveStopResumeTime(player.currentTime() * 1000))
    player.on('stop', () => saveStopResumeTime(player.currentTime() * 1000))
    player.on('seeked', () => saveStopResumeTime(player.currentTime() * 1000))

    player.on('ads-ad-ended', () => {
      convivaAdHandlers.handleAdEnded()
    })

    // workaround for subs button disappearing during ads
    player.on('ads-ad-started', () => {
      const { adBreaks } = player?.ssai()?.vmap
      const { pod, ad } = player?.ads
      // find unique breaks
      const breakTimes = new Set()
      adBreaks.forEach((adBreak) => {
        breakTimes.add(adBreak.timeOffset)
      })
      const breaksArray = [...breakTimes]
      const currentBreakId = breaksArray[pod.id]
      // convert break name to seconds
      const breakType = getBreakType(currentBreakId)

      convivaAdHandlers.handleAdStarted(ad, breakType)

      player.textTracks().tracks_.forEach((track) => {
        if (track.label === 'English') {
          player.controlBar.subsCapsButton?.removeClass('vjs-hidden')
        }
      })
    })

    // Workaround for subs appearing on as default
    // Snippet from https://player.support.brightcove.com/code-samples/brightcove-player-sample-automatically-set-caption-language.html
    let initialDisablingDone = false
    player.on('loadedmetadata', () => {
      let trackLanguage
      let i
      let theSubtitleTrack

      // Check if PiP button already exists before creating a new one
      if (!node.querySelector('.pip-button')) {
        const pipButton = buildPictureInPictureButton(player)
        if (pipButton) {
          const controlBarEl = node.querySelector('.vjs-control-bar')
          const fullscreenButton = controlBarEl.querySelector('.vjs-fullscreen-control')
          controlBarEl.insertBefore(pipButton, fullscreenButton)
        }
      }

      const tracks = player.textTracks()
      // eslint-disable-next-line no-plusplus
      for (i = 0; i < tracks.length; i++) {
        trackLanguage = tracks[i].language.substr(0, 2)
        if (trackLanguage) {
          // Check here if redux/user flag is set to enable subs by default - "showing"
          tracks[i].mode = subtitleState ? 'showing' : 'disabled'
          theSubtitleTrack = i
        }
      }

      if (isSafari()) {
        // Workaround for Safari text tracks issue
        tracks.addEventListener('change', () => {
          if (!initialDisablingDone) {
            // eslint-disable-next-line no-plusplus
            for (i = 0; i < tracks.length; i++) {
              if (tracks[i].mode === 'showing') {
                tracks[i].mode = 'disabled'
                theSubtitleTrack = i
                initialDisablingDone = true
              }
            }
          }
        })
      }

      const captionsButton = document.querySelector('button.vjs-subs-caps-button')

      if (captionsButton) {
        // Remove the existing subtitle options
        const subtitleOptions = document.querySelector('.vjs-subs-caps-button .vjs-menu')
        if (subtitleOptions) {
          subtitleOptions.remove()
        }

        // Function to update subtitle button state
        const updateSubtitleButtonState = () => {
          const isSubtitleOn = tracks[theSubtitleTrack].mode === 'showing'

          // Add or remove a class to indicate the on/off state
          captionsButton.classList.toggle('subtitles-on', isSubtitleOn)

          // Update the global subtitle state
          subtitleState = isSubtitleOn
        }

        // Add a click event listener to toggle subtitles on/off
        // Toggle subtitles on/off
        // eslint-disable-next-line no-unused-expressions
        // Update the subtitle button icon
        captionsButton.addEventListener('click', () => {
          tracks[theSubtitleTrack].mode = tracks[theSubtitleTrack].mode === 'showing' ? 'disabled' : 'showing'
          updateSubtitleButtonState()
        })

        // Update the subtitle button icon initially
        // Update the subtitle button state initially
        updateSubtitleButtonState()
      }
    })

    // Modify the loadstart event listener to apply the stored subtitle state
    player.on('loadstart', () => {
      const captionsButton = document.querySelector('.vjs-subs-caps-button')
      if (captionsButton) {
        const tracks = player.textTracks()
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
            tracks[i].mode = subtitleState ? 'showing' : 'disabled'
          }
        }
        captionsButton.classList.toggle('subtitles-on', subtitleState)
      }
    })

    player.one('play', () => {
      sendNestedGoogleTagEvent('content_vod_start', { vod_guid: content?.guid, vod_name: content?.programme?.name, vod_episode: content?.title })
    })

    function handlePlaying() {
      const currentTime = player.currentTime()
      window.parent.postMessage(
        {
          type: PLAYING,
          body: {
            time: currentTime,
          },
        },
        postMessageOrigin
      )
    }

    player.on('dispose', () => {
      player.off('timeupdate', onTimeUpdate)
    })

    if (videoData.resumePosition) {
      player.one('playing', () => {
        resumePlayback({
          player,
          resumePosition: videoData.resumePosition,
        })
      })
    }
  }

  /**
   * Seeks playhead to a give position after the supplied player is seekable.
   *
   * @param {  object  } player - Brightcove / Videojs Player object
   * @param {  integer  } resumePosition - seek position, in milliseconds
   * @param {  integer  } timeout - maximum time to wait for seekable, in milliseconds
   */
  function resumePlayback({ player, resumePosition, timeout = 500 }) {
    let seekTimer
    const targetTime = resumePosition / 1000
    if (targetTime <= 0) return

    const guardTimer = window.setTimeout(seek, timeout)

    function seekOnSeekable() {
      if (player.seekable().length) {
        seek()
      } else {
        seekTimer = window.setTimeout(seekOnSeekable, 16.667)
      }
    }

    function seek() {
      window.clearTimeout(seekTimer)
      window.clearTimeout(guardTimer)
      player.currentTime(targetTime)
    }

    seekOnSeekable()
  }

  /* Inject an element into a specified player, to give a play from start button
   * on the control bar.
   */
  function addRestartButton({ playerEl }) {
    const controlBarEl = playerEl.querySelector('.vjs-control-bar')
    const playButtonEl = controlBarEl.querySelector('.vjs-play-control')
    const restartButtonEl = document.createElement('button')

    restartButtonEl.setAttribute('title', 'Restart')

    restartButtonEl.className = 'vjs-control vjs-restart-button'
    restartButtonEl.onclick = () => window.videojs(playerEl).currentTime(0)

    controlBarEl.insertBefore(restartButtonEl, playButtonEl.nextSibling)
  }

  function startKantarAnalytics({ player }) {
    function getDuration() {
      return Math.round(parseInt(videoData.duration, 10) / 1000)
    }

    let position = 0

    function getPosition() {
      if (!player?.ads.isInAdMode()) {
        position = player.currentTime()
      }
      return position
    }

    kantarAnalytics({
      contentId: videoData.matId || 'matID_not_set',
      adapter: {
        getPosition,
        getDuration,
      },
    })
  }

  function dispatchStopResumeTimeToParentWindow({ time, clearEntry = false }) {
    window.parent.postMessage(
      {
        type: STOP_RESUME_SAVE_REQUEST,
        body: {
          time,
          clearEntry,
        },
      },
      postMessageOrigin
    )
  }

  function buildPictureInPictureButton(playerPIP) {
    const pipSupported = 'pictureInPictureEnabled' in document
    let webkitPipSupported = false

    if (!pipSupported && getBrowserName() === 'Apple Safari' && !isIphone()) {
      const testVideo = document.createElement('video')
      webkitPipSupported = testVideo.webkitSupportsPresentationMode && typeof testVideo.webkitSetPresentationMode === 'function'
    }

    if (!pipSupported && !webkitPipSupported) return null

    const button = document.createElement('button')
    button.innerHTML = `
      <span class="vjs-control-text">Picture in picture</span>
      <span class="theo-button-tooltip vjs-hidden">Picture in Picture</span>
    `
    button.classList.add('pip-button', 'vjs-control', 'vjs-button')
    let listenersAdded = false

    if (pipSupported) {
      button.addEventListener('click', async () => {
        const video = node.querySelector('video[src]')
        if (!listenersAdded) {
          video.addEventListener('enterpictureinpicture', () => trackEvent('pipOpenVod', {}))
          video.addEventListener('leavepictureinpicture', () => trackEvent('pipCloseVod', {}))
          listenersAdded = true
        }
        await video.requestPictureInPicture()
      })
    } else if (webkitPipSupported) {
      playerPIP.on('playing', () => {
        button.addEventListener('click', () => {
          const video = node.querySelector('video[src]')
          video.webkitSetPresentationMode('picture-in-picture')
          trackEvent('pipOpenVod', {})
        })
      })
    }

    return button
  }
}
